import React, { useState, useEffect } from 'react';
import { Box, Button, Flex, Image, Text } from 'rebass';
import { Input } from '@rebass/forms';
import { FiChevronDown, FiMapPin, FiTrash2, FiEdit } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { useTheme } from 'emotion-theming';
import { useToasts } from 'react-toast-notifications';
import { Link } from 'react-router-dom';

import NiceDropdown from 'components/NiceDropdown';
import consts from 'consts';
import NiceModal from '../NiceModal';
import { STUDY_LOCATION_TYPE } from 'utils/constants';
import config from 'config';

LocationSettings.propTypes = {
    study: PropTypes.object,
    studyUsers: PropTypes.array,
    account: PropTypes.object,
    onChange: PropTypes.func
};

/**
 * Generate study collabotator items for NiceSelect
 *
 * @param {array} studyCollaborators List of study collaborators
 * @param {Function} onCollaboratorSelect Study collaborator select callback
 * @returns
 */
function getStudyCollaboratorItems(studyCollaborators, onCollaboratorSelect) {
    const items = [];
    studyCollaborators.forEach(studyCollaborator => {
        items.push({
            id: studyCollaborator.user.id,
            title: studyCollaborator.user.name,
            onClick: () => onCollaboratorSelect(studyCollaborator.user.id)
        });
    });
    return items;
}

function LocationSettings(props) {
    const theme = useTheme();
    const { addToast } = useToasts();

    const [showLocationModal, setShowLocationModal] = useState();
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);
    const [locationInput, setLocationInput] = useState(props.study.location || '');

    const studyCollaboratorsWithZoom = props.studyUsers.filter(({ user }) => user.zoom_account_id != null);
    const isAccountZoomConnected = props.auth.account.zoom_account_id;
    const isUserZoomConnected = studyCollaboratorsWithZoom.length > 0;
    const isZoomConnected = isAccountZoomConnected || isUserZoomConnected;

    const zoomDropdownTitle = isZoomConnected ? (
        'Zoom'
    ) : (
        <>
            <Text display="inline" sx={{ pointerEvents: 'none', cursor: 'initial' }}>
                Zoom -{' '}
            </Text>
            {props.auth.user.zoom_account_id ? (
                <Link to={`/studies/${props.study.id}/settings#collaborators`} target="_blank">
                    Add study collaborator
                </Link>
            ) : (
                <a href={consts.zoomAuthorizationUrl} target="_blank">
                    Connect
                </a>
            )}
        </>
    );

    const isMsteamsConnected = props.auth.user.microsoft_calendar;
    const msteamsDropdownTitle = isMsteamsConnected ? (
        'MS Teams'
    ) : (
        <>
            <Text display="inline" sx={{ pointerEvents: 'none', cursor: 'initial' }}>
                MS Teams -{' '}
            </Text>
            <Link to={'/me#calendar-integrations'} target="_blank">
                Connect
            </Link>
        </>
    );

    const isMeetConnected = props.auth.user.google_calendar;
    const meetDropdownTitle = isMeetConnected ? (
        'Meet'
    ) : (
        <>
            <Text display="inline" sx={{ pointerEvents: 'none', cursor: 'initial' }}>
                Meet -{' '}
            </Text>
            <Link to={'/me#calendar-integrations'} target="_blank">
                Connect
            </Link>
        </>
    );

    const dropdownItems = [
        {
            id: STUDY_LOCATION_TYPE.CUSTOM,
            title: 'Custom location',
            icon: <FiMapPin style={{ marginLeft: '2px' }} />
        },
        {
            id: STUDY_LOCATION_TYPE.ZOOM,
            title: zoomDropdownTitle,
            icon: (
                <Image src={`${config.UI_SUFFIX}/zoom2.svg`} alt="Zoom" height="20px" sx={{ margin: '-2px 4px 0 0' }} />
            ),
            disabled: !isZoomConnected,
            submenu: isUserZoomConnected
                ? getStudyCollaboratorItems(studyCollaboratorsWithZoom, handleCollaboratorSelect)
                : null
        },
        {
            id: STUDY_LOCATION_TYPE.MSTEAMS,
            title: msteamsDropdownTitle,
            icon: (
                <Image
                    src={`${config.UI_SUFFIX}/msteams.png`}
                    alt="MS Teams"
                    height="20px"
                    sx={{ margin: '-2px 4px 0 0' }}
                />
            ),
            disabled: !isMsteamsConnected
        },
        {
            id: STUDY_LOCATION_TYPE.MEET,
            title: meetDropdownTitle,
            icon: (
                <Image src={`${config.UI_SUFFIX}/meet.png`} alt="Meet" height="20px" sx={{ margin: '-2px 4px 0 0' }} />
            ),
            disabled: !isMeetConnected
        }
    ];

    function getZoomLocationLabel() {
        const selectedStudyCollaborator = studyCollaboratorsWithZoom.find(
            studyCollaborator => studyCollaborator.user.id == props.study.location
        );
        if (!selectedStudyCollaborator) {
            return '';
        }

        return ` (${selectedStudyCollaborator.user.name})`;
    }

    useEffect(() => {
        setLocationInput(props.study.location);
    }, [props.study.location]);

    function handleLocationTypeSelect(id) {
        setShowLocationDropdown(false);

        if (id === 'custom') {
            setShowLocationModal(true);
        } else if ([STUDY_LOCATION_TYPE.ZOOM, STUDY_LOCATION_TYPE.MSTEAMS, STUDY_LOCATION_TYPE.MEET].includes(id)) {
            if (id == STUDY_LOCATION_TYPE.ZOOM && isUserZoomConnected) {
                addToast('Please select specific user as Zoom Meeting organizer', {
                    appearance: 'error',
                    autoDismiss: true
                });
            } else {
                const updatedStudy = { ...props.study, location_type: id };
                props.onChange(updatedStudy);
            }
        } else {
            addToast('Location type unknown', {
                appearance: 'error',
                autoDismiss: true
            });
        }
    }

    function handleCollaboratorSelect(id) {
        if (isUserZoomConnected) {
            // zoom integration with user-managed app
            // location is used to store user.id of study collaborator
            const updatedStudy = { ...props.study, location_type: STUDY_LOCATION_TYPE.ZOOM, location: String(id) };
            props.onChange(updatedStudy);
        } else {
            addToast('Zoom is not integrated with any study collaborator.', {
                appearance: 'error',
                autoDismiss: true
            });
        }
    }

    function saveLocation() {
        if (!locationInput.trim()) {
            return;
        }
        setShowLocationModal(false);
        props.onChange({
            ...props.study,
            location_type: STUDY_LOCATION_TYPE.CUSTOM,
            location: locationInput
        });
    }

    function deleteLocation() {
        props.onChange({
            ...props.study,
            location_type: null,
            location: null
        });
    }

    function explainer(message) {
        return (
            <Box mt={1} className={'fs-12 text-secondary'}>
                {message}
            </Box>
        );
    }

    function locationActionButtons() {
        return (
            <Flex ml={3} flexWrap="nowrap" minWidth="max-content">
                {props.study.location_type == STUDY_LOCATION_TYPE.CUSTOM && (
                    <Button variant="secondary-gray" ml={2} onClick={() => setShowLocationModal(true)}>
                        <FiEdit /> Edit
                    </Button>
                )}
                <Button variant="secondary-gray" className="secondary-icon" ml={1} onClick={() => deleteLocation()}>
                    <FiTrash2 />
                </Button>
            </Flex>
        );
    }

    return (
        <>
            <Box mb={2}>
                {!props.study.location_type && (
                    <Flex sx={{ position: 'relative' }}>
                        <Button
                            name="location"
                            variant="secondary"
                            mr={2}
                            onClick={() => setShowLocationDropdown(true)}
                        >
                            Add a location{' '}
                            <FiChevronDown
                                style={{
                                    margin: '5px 0 0 0'
                                }}
                            />
                        </Button>
                        {showLocationDropdown && (
                            <NiceDropdown
                                hasSubmenu={isUserZoomConnected}
                                onClose={() => setShowLocationDropdown(false)}
                                items={dropdownItems}
                                onChange={value => handleLocationTypeSelect(value)}
                            />
                        )}
                    </Flex>
                )}
                {props.study.location_type === STUDY_LOCATION_TYPE.ZOOM && (
                    <Box color={theme.colors.black} fontSize={1}>
                        <Flex>
                            <Flex alignItems="center">
                                <Image src={`${config.UI_SUFFIX}/zoom2.svg`} alt="Zoom" height="20px" />
                                <Box ml={2}>
                                    Zoom
                                    {getZoomLocationLabel(props.study.location)}
                                </Box>
                            </Flex>
                            {locationActionButtons()}
                        </Flex>
                        {explainer('Each invite includes a unique meeting link.')}
                    </Box>
                )}
                {props.study.location_type === STUDY_LOCATION_TYPE.MSTEAMS && (
                    <Box color={theme.colors.black} fontSize={1}>
                        <Flex>
                            <Flex alignItems="center">
                                <Image src={`${config.UI_SUFFIX}/msteams.png`} alt="MS Teams" height="20px" />
                                <Box ml={2}>MS Teams</Box>
                            </Flex>
                            {locationActionButtons()}
                        </Flex>
                        {explainer('Each invite includes a unique meeting link.')}
                    </Box>
                )}
                {props.study.location_type === STUDY_LOCATION_TYPE.MEET && (
                    <Box color={theme.colors.black} fontSize={1}>
                        <Flex>
                            <Flex alignItems="center">
                                <Image src={`${config.UI_SUFFIX}/meet.png`} alt="MS Teams" height="20px" />
                                <Box ml={2}>Meet</Box>
                            </Flex>
                            {locationActionButtons()}
                        </Flex>
                        {explainer('Each invite includes a unique meeting link.')}
                    </Box>
                )}
                {props.study.location_type === STUDY_LOCATION_TYPE.CUSTOM && (
                    <Box color={theme.colors.black} fontSize={1}>
                        <Flex>
                            <Flex alignItems="center">
                                <FiMapPin style={{ minWidth: '14px' }} />{' '}
                                <Box className="ellipsis" ml={2}>
                                    {props.study.location}
                                </Box>
                            </Flex>
                            {locationActionButtons()}
                        </Flex>
                    </Box>
                )}
            </Box>
            <NiceModal
                isOpen={showLocationModal}
                shouldCloseOnOverlayClick
                onRequestClose={() => setShowLocationModal(false)}
                title="Edit Location"
            >
                <Box>
                    <Box mb={3} sx={{ position: 'relative', marginTop: '1px' }}>
                        <Input
                            ref={input => input && input.focus()}
                            placeholder="Location"
                            value={locationInput || ''}
                            onChange={e => setLocationInput(e.target.value)}
                            onKeyDown={e => (e.key === 'Enter' ? saveLocation() : null)}
                            required
                        />
                    </Box>
                    <Box className="modal-actions">
                        <Button variant="secondary-gray" onClick={() => setShowLocationModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => saveLocation()}
                            ml={3}
                            mr={0}
                            disabled={!locationInput}
                        >
                            Save
                        </Button>
                    </Box>
                </Box>
            </NiceModal>
        </>
    );
}

export default LocationSettings;
